import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Message from "messaging/message";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import CommentSchema, { CommentJSON, CommentParams } from "work/proposals/comments/_schemas/comment-schema";
import CommentThread from "work/proposals/comments/threads/comment-thread";

type MarkFlags = {
  markedForCreation?: boolean;
  markedForPublish?: boolean;
  markedForUnpublish?: boolean;
  markedForEdit?: boolean;
  markedForReadReceipt?: boolean;
  markedForUnread?: boolean;
  markedForDeletion?: boolean;
};

export default class Comment {
  public static readonly EXAMPLE: Comment = new Comment({
    id: Guid.generate(),
    senderId: Guid.generate(),
    thread: CommentThread.EXAMPLE,
    content: "This is a test comment.",
    created: new AHDate(),
    published: new AHDate(),
    issues: [],
    readReceipts: [],
  });

  public static create(params: CommentParams): Comment {
    const parsed = CommentSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid Comment parameters.");
    }
    return new Comment(parsed);
  }

  public static fromJSON(json: CommentJSON): Comment {
    if (isNullOrUndefined(json)) return json;
    const params = CommentSchema.deserialize(json);
    return new Comment(params);
  }

  public static fromMessage(message: Message): Comment {
    return new Comment({
      ...message,
      // `deleted` is a getter over a private field, so it is not an own property and the spread above
      // silently drops it. Every comment therefore arrived un-deleted, which is why the "this comment
      // was deleted" render could never be reached even once the server started sending tombstones.
      deleted: message.deleted,
      attachmentCount: message.attachmentCount,
      thread: CommentThread.fromForum(message.forum),
    });
  }

  public readonly id?: Guid;
  public readonly senderId: Guid | null;
  public readonly thread: CommentThread;
  public readonly content: string;
  /**
   * How many attachments the comment carries. On a deleted comment this is all that is left of them:
   * the tombstone indicates attachments without presenting them, so there is nothing to open.
   */
  public readonly attachmentCount: number;
  public readonly created?: AHDate;
  public readonly published?: AHDate;

  public readonly readReceipts: ReadReceipt[] = [];
  public readonly issues: string[] = [];

  public readonly deleted?: AHDate;
  public readonly updated?: AHDate;
  public readonly retracted?: AHDate;

  public readonly markedForCreation?: boolean;
  public readonly markedForPublish?: boolean;
  public readonly markedForUnpublish?: boolean;
  public readonly markedForEdit?: boolean;
  public readonly markedForReadReceipt?: boolean;
  public readonly markedForUnread?: boolean;
  public readonly markedForDeletion?: boolean;

  /**
   * Whether this comment is carrying work its author has not delivered yet.
   *
   * A tombstone never is. Deleting is the last thing that can happen to a comment, so whatever was
   * outstanding about it — unpublished, edited, marked for deletion — stopped mattering when it was
   * deleted. Without this a deleted author-only draft stayed `!published` forever and went on being
   * counted as pending work by every surface that asks: the pane's own pending dot, and the comment
   * button's badge (`getPendingCommentsCount`), which would sit on a proposal advertising undelivered
   * work whose only remaining trace says "This comment was deleted".
   */
  public get isPending(): boolean {
    // A retraction tombstone is not pending either — it is not the reader's work, it is the news that
    // somebody else took theirs back. Only its author holds it as a draft, and only their copy has a
    // body to hold.
    if (this.isDeleted || this.isRetractedTombstone) return false;
    return Boolean(
      !this.published ||
      this.retracted ||
      this.markedForEdit ||
      this.markedForReadReceipt !== undefined ||
      this.markedForUnread !== undefined ||
      this.markedForDeletion ||
      this.markedForCreation ||
      this.markedForUnpublish,
    );
  }

  /**
   * True when this comment exists on the server but is not visible to the audience (an author-only
   * draft) — either because it was never published, or because its author took it back.
   *
   * `!published` alone used to say this, and stopped being able to when `published` became write-once:
   * a retracted comment keeps the instant it first went out, so that it holds its place in the thread
   * rather than re-entering the conversation as the newest thing in it. `retracted` is what withholds
   * it now, and it is the half that has to be tested here — without it a retracted comment reads as
   * live, offers its author no Publish button, and is counted as delivered work.
   *
   * A tombstone is not a draft, for the same reason it is not pending: a draft is something its author
   * can still publish, and a deleted comment is not. `deleted` and `!published` are both true of a
   * comment deleted while it was still a draft — the state {@link CommentSchema} now carries end to end
   * — so without this the drafts a surface reports as "your undelivered work" included comments that no
   * longer exist and that no Publish control can ever send.
   */
  public get isUnpublishedDraft(): boolean {
    return Boolean(
      this.id &&
        !this.markedForCreation &&
        !this.isVisibleToAudience &&
        !this.isDeleted &&
        !this.isRetractedTombstone,
    );
  }

  /**
   * Whether anyone but the author may read this comment: it has been published, and not taken back
   * since.
   *
   * The one place the client spells that rule, mirroring `Message.IsPublishedToAudience` on the server
   * so the two cannot drift. Every surface that used to ask `Boolean(comment.published)` meant this.
   */
  public get isVisibleToAudience(): boolean {
    return Boolean(this.published && !this.retracted);
  }

  /**
   * When this comment took its place in the conversation, which is what orders it against the others:
   * the moment its audience got it, or — for a draft nobody else has — the moment its author wrote it.
   *
   * `published` rather than `updated` because the moment is write-once: editing a comment does not
   * re-enter it into the conversation as the newest thing in it, and neither does retracting one.
   *
   * `undefined` means NEITHER has happened yet, which is true of exactly one comment: the one the
   * author has just posted on this screen, shown optimistically while its write is still on the wire.
   * That comment is the newest thing in the conversation, and every surface that orders comments has to
   * say so itself — a missing moment read as zero is what put a comment at the BOTTOM of the pane the
   * instant it was posted, where it sat until the server's answer arrived and it jumped to the top
   * (#5125).
   */
  public get enteredConversationAt(): AHDate | undefined {
    return this.published ?? this.created;
  }

  /**
   * An author-only draft the author published once and then took back, as against one they have simply
   * never published.
   *
   * The two used to be the same state — both were just `!published` — so the save confirmation's
   * "publish my pending comments" choice, which publishes every draft the actor holds, re-delivered a
   * comment its author had deliberately retracted on the next unrelated save. Retracting now records
   * itself (`Message.Retracted`), and a bulk publish skips these; the per-comment Publish button is
   * unaffected, because that is the author saying so about this comment.
   */
  public get isRetractedDraft(): boolean {
    return Boolean(this.isUnpublishedDraft && this.retracted);
  }

  /**
   * True on the copy of a retracted comment its AUDIENCE holds: a tombstone with no body, standing
   * where the comment still sits, captioned with when it was removed.
   *
   * Its author holds the same comment with `retracted` set and its text intact, so the two are told
   * apart by whether there is anything to read — which is a fact about the payload the server chose to
   * send this reader, not a guess. A deleted comment is deleted whatever else was true of it, so that
   * tombstone wins.
   */
  public get isRetractedTombstone(): boolean {
    return Boolean(this.retracted && !this.isDeleted && !this.content);
  }

  /** Soft delete flag */
  public get isDeleted(): boolean {
    return !!this.deleted;
  }

  public constructor(params: CommentParams) {
    const {
      thread,
      content,
      attachmentCount,
      senderId,
      id,
      created,
      published,
      issues,
      readReceipts,
      deleted,
      updated,
      retracted,
      markedForCreation,
      markedForPublish,
      markedForUnpublish,
      markedForEdit,
      markedForReadReceipt,
      markedForUnread,
      markedForDeletion,
    } = params as CommentParams & MarkFlags;

    // A withheld comment has no body — the server hydrates it from a type that has none — and it is
    // still a comment: it keeps its place in the thread as "this comment was deleted", or, for one its
    // author took back, as "Removed at ...". Only a live comment has to say something.
    if (!content && !deleted && !retracted) throw new EmptyCommentError();

    this.thread = thread instanceof CommentThread ? thread : new CommentThread(thread);
    this.id = id ?? Guid.generate();
    this.senderId = senderId ?? null;
    this.content = content ?? "";
    this.attachmentCount = attachmentCount ?? 0;
    this.created = created;
    this.published = published;

    this.issues = issues ?? [];
    this.readReceipts = readReceipts ?? [];

    this.deleted = deleted;
    this.updated = updated;
    this.retracted = retracted;

    // Flags
    this.markedForCreation = markedForCreation ?? (id ? undefined : true);
    this.markedForPublish = markedForPublish;
    this.markedForUnpublish = markedForUnpublish;
    this.markedForEdit = markedForEdit;
    this.markedForReadReceipt = markedForReadReceipt;
    this.markedForUnread = markedForUnread;
    this.markedForDeletion = markedForDeletion;

    Object.freeze(this);
  }

  public toJSON(): CommentJSON {
    return CommentSchema.serialize(this);
  }

  public with(patch: Partial<CommentParams> & MarkFlags): Comment {
    return new Comment({
      ...(this as unknown as CommentParams),
      issues: [...this.issues],
      readReceipts: [...this.readReceipts],
      ...patch,
    });
  }

  public markForCreation(): Comment {
    return this.with({ markedForCreation: true });
  }

  public unmarkForCreation(): Comment {
    return this.with({ markedForCreation: false });
  }

  public markForPublish(): Comment {
    return this.with({ markedForPublish: true, markedForUnpublish: false });
  }

  public unmarkForPublish(): Comment {
    return this.with({ markedForPublish: false });
  }

  /** Retract an already-published comment back to an author-only draft. */
  public markForUnpublish(): Comment {
    return this.with({ markedForUnpublish: true, markedForPublish: false });
  }

  public unmarkForUnpublish(): Comment {
    return this.with({ markedForUnpublish: false });
  }

  public markForEdit(): Comment {
    return this.with({
      markedForEdit: true,
      updated: new AHDate(),
    });
  }

  public markForDeletion(): Comment {
    return this.with({
      markedForDeletion: true,
      deleted: new AHDate(),
    });
  }

  public unmarkForDeletion(): Comment {
    return this.with({
      markedForDeletion: false,
      deleted: undefined,
    });
  }

  /**
   * Marks this comment unread for the reader.
   *
   * It used to CANCEL a staged read receipt instead of replacing it — returning a comment with both
   * flags false — and its sibling {@link markForReadReceipt} did the same in the other direction. That
   * comment carries no intent at all, and `toMessage` turns it into a bulk entry with no
   * markRead/markUnread/markDeleted/markCreated set: the server reads such an entry as a write to the
   * message itself and edits it, or — if the comment's thread carries no forum id, which is what a
   * freshly composed comment has — as a re-post, soft-deleting the comment and minting a replacement.
   * A read-state toggle must never be able to say either of those things, so each of these two now
   * states its own intent and clears the other's.
   */
  public markForUnread(): Comment {
    return this.with({ markedForUnread: true, markedForReadReceipt: false });
  }

  public edit(content?: string): Comment {
    return this.with({
      content,
      updated: new AHDate(),
      markedForEdit: this.published ? true : undefined,
    });
  }

  public setDeleted(): Comment {
    return this.with({
      deleted: new AHDate(),
      markedForDeletion: true,
    });
  }

  public setUndeleted(): Comment {
    return this.with({
      deleted: undefined,
      markedForDeletion: false,
    });
  }

  /** Marks this comment read for the reader. See {@link markForUnread} for why it clears rather than cancels. */
  public markForReadReceipt(): Comment {
    return this.with({ markedForReadReceipt: true, markedForUnread: false });
  }

  public publish(): Comment {
    return this.with({ markedForPublish: true });
  }

  public isReadByUser(userId?: Guid): boolean {
    if (!userId) return false;
    return this.readReceipts.some((receipt) => receipt.userId.isEqualTo(userId));
  }

  /**
   * Whether this comment is still waiting to be read by `userId`.
   *
   * A tombstone never is. You cannot have unread content that no longer exists: the server leaves a
   * deleted message out of `Forum.NumUnreadMessages`, and a read receipt aimed at a deleted row is a
   * write it discards. It is also hydrated from a type that has no read receipts, so "has this reader a
   * receipt for it" answers no for ever — which is what left the pane's Mark all read permanently
   * offered, and permanently ineffective, as soon as one deleted comment was in the thread.
   *
   * One getter rather than the same three clauses in each caller: the pane's row, its Mark all read
   * control and the mark-read sweep have to agree about what is unread, or the pane advertises work
   * that pressing the button cannot clear.
   */
  public isUnreadBy(userId?: Guid): boolean {
    // Nor is a retraction tombstone: the server drops a retracted message from `Forum.NumUnreadMessages`
    // for the same reason it drops a deleted one, so counting it here would put a dot on a row no badge
    // counts and offer a Mark Read the author has taken the subject of away.
    if (this.isDeleted || this.isRetractedTombstone) return false;
    // Nor is your own comment. You have read what you wrote, which is why the row already refuses to
    // draw an unread dot on it (`!isOwnComment` in `CommentItem`) and why the server leaves it out of
    // `Forum.NumUnreadMessages` (`m.SenderId != CurrentUser`). Without the rule HERE those two agreed
    // and this getter did not, so the mark-read sweep wrote a read receipt for a comment nothing shows
    // as unread — an invisible write, and the one that raced. Opening the pane fired it at the same
    // comment the author was retracting; its response, sent before the retraction and carrying
    // `retracted: null`, arrived 62ms after the retraction's and upserted the whole comment over it.
    // The row went back to reading as published, with no Publish button, until a reload. See CircleCI
    // 54188/54218.
    if (this.isSentBy(userId)) return false;
    return !this.isReadByUser(userId) && !this.markedForReadReceipt;
  }

  /** Whether `userId` wrote this comment. A comment with no sender is system narration nobody wrote. */
  public isSentBy(userId?: Guid): boolean {
    if (!userId || !this.senderId) return false;
    return this.senderId.isEqualTo(userId);
  }

  public toMessage(params: { isViewingAsVendor: boolean }): Message {
    const { isViewingAsVendor } = params;
    const message = Message.create({
      ...this,
      id: this.id ?? Guid.generate(),
      forum: this.thread.toForum({ isViewingAsVendor }),
    });
    if (this.markedForCreation) {
      message.markForCreation();
    }
    if (this.markedForPublish) {
      message.publish();
    }
    if (this.markedForUnpublish) {
      message.unpublish();
    }
    if (this.markedForReadReceipt) {
      message.markRead();
    }
    if (this.markedForUnread) {
      message.markUnread();
    }
    if (this.markedForDeletion) {
      message.delete();
    }

    return message;
  }

  public isEqualTo(other: Comment): boolean {
    if (this.id && other.id) {
      return this.id.isEqualTo(other.id);
    }
    return Boolean(
      this.thread.isEqualTo(other.thread) && this.content === other.content && this.senderId?.isEqualTo(other.senderId),
    );
  }
}

export class EmptyCommentError extends Error {
  constructor() {
    super(`Comment cannot be empty`);
  }
}
